/**
 * Props — vendor booths, porta-potties, fencing, signage.
 *
 * All instanced where possible. Placed by Layout.ts using a small list of
 * positions per landmark.
 */

import * as THREE from 'three';
import type { Terrain } from '@/world/Terrain';

export interface VendorRow {
  startX: number;
  startZ: number;
  endX: number;
  endZ: number;
  count: number;
}

export class Props {
  static buildVendorRow(terrain: Terrain, row: VendorRow): THREE.Group {
    const group = new THREE.Group();
    group.name = 'VendorRow';

    const matBody = new THREE.MeshStandardMaterial({
      color: '#ddd6c8',
      roughness: 0.85
    });
    const matSign = new THREE.MeshStandardMaterial({
      color: '#ffc371',
      roughness: 0.6,
      emissive: '#ffc371',
      emissiveIntensity: 0.2
    });

    const colors = ['#cc4040', '#3a76c8', '#3a9a4a', '#d4a830', '#7a3aa8', '#cc6a30'];

    for (let i = 0; i < row.count; i++) {
      const t = i / Math.max(1, row.count - 1);
      const x = row.startX + (row.endX - row.startX) * t;
      const z = row.startZ + (row.endZ - row.startZ) * t;
      const y = terrain.heightAt(x, z);

      const booth = new THREE.Group();
      booth.position.set(x, y, z);

      const body = new THREE.Mesh(
        new THREE.BoxGeometry(2.6, 2.0, 2.0),
        matBody
      );
      body.position.y = 1.0;
      body.castShadow = true;
      booth.add(body);

      // Striped roof — pick a color from the palette
      const roof = new THREE.Mesh(
        new THREE.BoxGeometry(3.0, 0.3, 2.4),
        new THREE.MeshStandardMaterial({ color: colors[i % colors.length] })
      );
      roof.position.y = 2.15;
      booth.add(roof);

      // Sign
      const sign = new THREE.Mesh(
        new THREE.BoxGeometry(2.4, 0.5, 0.1),
        matSign
      );
      sign.position.set(0, 2.45, 1.05);
      booth.add(sign);

      // Counter
      const counter = new THREE.Mesh(
        new THREE.BoxGeometry(2.6, 0.15, 0.4),
        matBody
      );
      counter.position.set(0, 1.0, 1.0);
      booth.add(counter);

      // Match the row direction
      const angle = Math.atan2(row.endX - row.startX, row.endZ - row.startZ);
      booth.rotation.y = angle;

      group.add(booth);
    }

    return group;
  }

  static buildPortaPottyRow(terrain: Terrain, x: number, z: number, count: number): THREE.Group {
    const group = new THREE.Group();
    const matCase = new THREE.MeshStandardMaterial({ color: '#3a76c8', roughness: 0.7 });
    const matRoof = new THREE.MeshStandardMaterial({ color: '#1a4860', roughness: 0.8 });
    for (let i = 0; i < count; i++) {
      const px = x + i * 1.4;
      const py = terrain.heightAt(px, z);
      const body = new THREE.Mesh(new THREE.BoxGeometry(1.1, 2.2, 1.2), matCase);
      body.position.set(px, py + 1.1, z);
      body.castShadow = true;
      group.add(body);
      const roof = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.15, 1.3), matRoof);
      roof.position.set(px, py + 2.25, z);
      group.add(roof);
    }
    return group;
  }

  static buildFencePerimeter(
    terrain: Terrain,
    points: { x: number; z: number }[]
  ): THREE.Group {
    const group = new THREE.Group();
    group.name = 'Fence';
    const matPost = new THREE.MeshStandardMaterial({ color: '#3a3a3a', metalness: 0.7, roughness: 0.5 });
    const matMesh = new THREE.MeshStandardMaterial({
      color: '#888888',
      transparent: true,
      opacity: 0.45,
      side: THREE.DoubleSide
    });

    for (let i = 0; i < points.length; i++) {
      const a = points[i];
      const b = points[(i + 1) % points.length];
      const dx = b.x - a.x;
      const dz = b.z - a.z;
      const len = Math.hypot(dx, dz);
      const angle = Math.atan2(dx, dz);
      const sectionCount = Math.ceil(len / 4);
      for (let s = 0; s <= sectionCount; s++) {
        const t = s / sectionCount;
        const px = a.x + dx * t;
        const pz = a.z + dz * t;
        const py = terrain.heightAt(px, pz);
        const post = new THREE.Mesh(new THREE.BoxGeometry(0.12, 2.1, 0.12), matPost);
        post.position.set(px, py + 1.05, pz);
        group.add(post);
      }
      // Mesh panel
      const panel = new THREE.Mesh(
        new THREE.PlaneGeometry(len, 1.8),
        matMesh
      );
      const midX = (a.x + b.x) / 2;
      const midZ = (a.z + b.z) / 2;
      const midY = terrain.heightAt(midX, midZ);
      panel.position.set(midX, midY + 1.0, midZ);
      panel.rotation.y = angle + Math.PI / 2;
      group.add(panel);
    }

    return group;
  }
}
